import React, { useRef, useLayoutEffect, useState, useEffect, useCallback, useMemo } from "react";
import { Box, Snackbar, Alert, IconButton, Zoom, Badge } from "@mui/material";
import { ArrowRight2 } from "iconsax-react";
import { useDispatch, useSelector } from "react-redux";
import { selectMessages, selectContactSelected, selectSelectedInboxTab, selectContactID } from "../../redux/selectors";
import ChatboxNavbar from "./Features/ChatboxNavbar";
import ChatBody from "./Features/ChatBody";
import TextBox from "./Features/TextBox"; // Import TextBox component
import { clearErrors } from "../../redux/Slice/Inbox/InboxMessageSlice";
import { clearError as clearTeamMemberError } from "../../redux/Slice/Inbox/teamMemberChatSlice";
import { isSubscriptionBlockedError } from "../../utils/subscriptionError";
import { slimScrollbarSx } from "../../utils/scrollbarStyles";

/** Pixels from bottom to still count as "at bottom" (hide FAB). */
const SCROLL_BOTTOM_THRESHOLD_PX = 120;

// hideNavbar (2026-08-21): true only when the XL layout is rendering its own
// ChatboxNavbar externally, spanning the chat column AND the ChatInfoRail
// beside it — see Inbox.jsx. ChatBox keeps rendering its own internal navbar
// exactly as before whenever this is false/omitted (every other breakpoint,
// or XL with no contact selected / a team-member chat).
function ChatBox({ openDialog, setOpenDialog, hideNavbar = false }) {
  const dispatch = useDispatch();
  // Check if a contact is selected by accessing the messages from Redux
  const messages = useSelector(selectMessages);
  const contactSelected = useSelector(selectContactSelected);
  const selectedContactID = useSelector(selectContactID);

  // Check if team member chat is active
  const currentTeamMemberChat = useSelector((state) => state.teamMemberChat?.currentChat);
  const selectedInboxTab = useSelector(selectSelectedInboxTab);
  const isTeamMemberChat = !!(currentTeamMemberChat?.otherMemberId || currentTeamMemberChat?.otherMember?.id);
  // Also check if we're in "Chat with Members" tab (even if no team member is selected)
  const isChatWithMembersTab = selectedInboxTab === "Chat with Members";

  // Error handling for Toast (Snackbar)
  const inboxMessageError = useSelector((state) => state.inboxMessage?.error);
  const teamMemberChatError = useSelector((state) => state.teamMemberChat?.currentChat?.error);
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [droppedFilesPayload, setDroppedFilesPayload] = useState({ files: [], stamp: 0 });

  // Customer message errors: clear silently when no chat is open; otherwise show Snackbar (team errors unchanged).
  useEffect(() => {
    const error = isTeamMemberChat ? teamMemberChatError : inboxMessageError;
    if (!error) return;
    if (!isTeamMemberChat && !contactSelected) {
      dispatch(clearErrors());
      return;
    }
    const msg = typeof error === 'string' ? error : (error.message || "An error occurred");
    if (isSubscriptionBlockedError(msg)) {
      // Axios interceptor already showed a toast; clear Redux error silently
      if (isTeamMemberChat) {
        dispatch(clearTeamMemberError());
      } else {
        dispatch(clearErrors());
      }
      return;
    }
    setErrorMessage(msg);
    setSnackbarOpen(true);
  }, [dispatch, inboxMessageError, teamMemberChatError, isTeamMemberChat, contactSelected]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
    // Clear errors in Redux when toast is closed or expires
    if (isTeamMemberChat) {
      dispatch(clearTeamMemberError());
    } else {
      dispatch(clearErrors());
    }
  };

  // Remount when sidebar tab or team thread changes — not on every customer contact switch (preserves scroll/behavior)
  //
  // BUG FIX (closing a chat gets permanently stuck on the loading skeleton): MessageCard.jsx
  // auto-corrects selectedInboxTab to "Closed"/"Blocked Contacts" (flagged via
  // skipNextTabChangeClear) the instant the currently open chat lands in that category, purely
  // to keep the sidebar highlight honest — it is NOT the user switching tabs. But keying
  // ChatBody by the raw selectedInboxTab treated it exactly like a real switch, force-remounting
  // ChatBody; its unmount cleanup (clearAllChatStates) wipes messages/contactInfo, and nothing
  // ever repopulates them for the still-selected contact — leaving the header/body stuck on
  // their loading skeletons forever. Track the tab value used for keying separately, and only
  // advance it on a genuine switch (skipNextTabChangeClear false), so this auto-correction
  // leaves ChatBody mounted and showing the just-closed chat, same as Rahim's original intent
  // (2026-09-08: "the chat should remain there, it's fine to show it").
  const skipNextTabChangeClear = useSelector((state) => state.inbox.skipNextTabChangeClear);
  const [prevInboxTabForKey, setPrevInboxTabForKey] = useState(selectedInboxTab);
  const [effectiveInboxTabForKey, setEffectiveInboxTabForKey] = useState(selectedInboxTab);
  if (selectedInboxTab !== prevInboxTabForKey) {
    setPrevInboxTabForKey(selectedInboxTab);
    if (!skipNextTabChangeClear) {
      setEffectiveInboxTabForKey(selectedInboxTab);
    }
  }
  const chatBodyKey = [
    effectiveInboxTabForKey,
    isTeamMemberChat ? `tm-${currentTeamMemberChat?.otherMemberId || ""}` : "cust",
  ].join("-");

  const chatBoxRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [, setChatBoxWidth] = useState(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const scrollRafRef = useRef(false);

  const teamMemberMessageCount = useSelector(
    (state) => state.teamMemberChat?.currentChat?.messages?.length ?? 0
  );

  // How many new messages arrived while the user was scrolled away from the
  // bottom (Rahim, 2026-08-29: "it doesnt tell how many new messages are
  // below"). Tracked by the NEWEST message's own stable identity, not raw
  // array length — pagination loading older messages also changes length,
  // but doesn't change which message is last (chats are sorted oldest-first,
  // confirmed in ChatBody.jsx's own sort), so this can't miscount a history
  // page-load as new incoming messages.
  const [newBelowCount, setNewBelowCount] = useState(0);
  const lastSeenMessageKeyRef = useRef(null);
  const prevChatBodyKeyRef = useRef(chatBodyKey);

  const newestUserMessageKey = useMemo(() => {
    const arr = messages?.chats;
    const last = arr && arr.length ? arr[arr.length - 1] : null;
    return last ? (last.id || last._id || last.tempId || null) : null;
  }, [messages?.chats]);

  // Same shape assumption as ChatBody.jsx's own id||_id||tempId fallback
  // chain (both reuse the same message model) — a scalar selector, not the
  // whole array, so this only re-renders when the actual newest message
  // changes, not on every unrelated team-member-chat state update.
  const newestTeamMemberMessageKey = useSelector((state) => {
    const arr = state.teamMemberChat?.currentChat?.messages;
    const last = arr && arr.length ? arr[arr.length - 1] : null;
    return last ? (last.id || last._id || last.tempId || null) : null;
  });

  const newestMessageKey = isTeamMemberChat ? newestTeamMemberMessageKey : newestUserMessageKey;

  const updateScrollToBottomVisibility = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      setShowScrollToBottom(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const hasOverflow = scrollHeight > clientHeight + 2;
    const scrolledUp = distanceFromBottom > SCROLL_BOTTOM_THRESHOLD_PX;
    setShowScrollToBottom(hasOverflow && scrolledUp);
    // Reaching the bottom on your own (not just clicking the button) clears
    // the count too — there's nothing left to call out once you're there.
    if (!scrolledUp && newBelowCount > 0) setNewBelowCount(0);
  }, [newBelowCount]);

  // Increment on a genuinely new message while scrolled away from the bottom.
  // Reads the live DOM directly (not the showScrollToBottom state above, which
  // is only recomputed a frame later via the rAF-scheduled effect elsewhere)
  // so a message arriving in the same tick as this check can't be undercounted.
  useEffect(() => {
    if (prevChatBodyKeyRef.current !== chatBodyKey) {
      // Switched conversation — this is a fresh baseline, not a "new" message.
      prevChatBodyKeyRef.current = chatBodyKey;
      lastSeenMessageKeyRef.current = newestMessageKey;
      setNewBelowCount(0);
      return;
    }
    if (!newestMessageKey || newestMessageKey === lastSeenMessageKeyRef.current) return;
    lastSeenMessageKeyRef.current = newestMessageKey;

    const el = scrollContainerRef.current;
    const scrolledAway = el
      ? el.scrollHeight - el.scrollTop - el.clientHeight > SCROLL_BOTTOM_THRESHOLD_PX
      : false;
    if (scrolledAway) setNewBelowCount((c) => c + 1);
  }, [newestMessageKey, chatBodyKey]);

  const onChatScroll = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = true;
    requestAnimationFrame(() => {
      scrollRafRef.current = false;
      updateScrollToBottomVisibility();
    });
  }, [updateScrollToBottomVisibility]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", onChatScroll, { passive: true });
    const ro = new ResizeObserver(() => {
      updateScrollToBottomVisibility();
    });
    ro.observe(el);
    updateScrollToBottomVisibility();
    return () => {
      el.removeEventListener("scroll", onChatScroll);
      ro.disconnect();
    };
  }, [onChatScroll, updateScrollToBottomVisibility, chatBodyKey]);

  useEffect(() => {
    const id = requestAnimationFrame(() => updateScrollToBottomVisibility());
    return () => cancelAnimationFrame(id);
  }, [
    messages?.chats?.length,
    teamMemberMessageCount,
    chatBodyKey,
    updateScrollToBottomVisibility,
  ]);

  const handleScrollToBottomClick = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setNewBelowCount(0);
  }, []);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!contactSelected && !isTeamMemberChat) return;
    if (event.dataTransfer?.types?.includes("Files")) {
      setIsDragActive(true);
    }
  }, [contactSelected, isTeamMemberChat]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!contactSelected && !isTeamMemberChat) return;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  }, [contactSelected, isTeamMemberChat]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    if (!contactSelected && !isTeamMemberChat) return;
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    setDroppedFilesPayload({
      files,
      stamp: Date.now(),
    });
  }, [contactSelected, isTeamMemberChat]);

  const handleDroppedFilesConsumed = useCallback((stamp) => {
    setDroppedFilesPayload((prev) => {
      if (!prev?.stamp || prev.stamp !== stamp) return prev;
      return { files: [], stamp: 0 };
    });
  }, []);

  // Measure ChatBox width and update CSS custom property
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (chatBoxRef.current) {
        const width = chatBoxRef.current.offsetWidth;
        setChatBoxWidth(width);
        // Set CSS custom property with actual pixel value
        chatBoxRef.current.style.setProperty('--chatbox-width', `${width}px`);
      }
    };

    // Initial measurement
    updateWidth();

    // Create ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (chatBoxRef.current) {
      resizeObserver.observe(chatBoxRef.current);
    }

    // Also listen to window resize
    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // NOTE: previously bailed out here when contactSelected && !contactExists ("deleted
  // contact" guard). Removed — contactSelected already implies messages are loaded for
  // this contact (selectContactID reads messages.contactID), so contactExists=false at
  // that point only ever meant "not in the currently loaded page of the paginated inbox
  // list", not "deleted". That falsely blanked the chat (leaving ChatInfo visible) for any
  // real, open contact who'd scrolled past page 1 — see ChatBody.jsx's identical fix
  // (search "contactExists may be false" there). Genuine deletion/block already navigates
  // away explicitly (see MessageCard.jsx's delete/block/unblock handlers), so this guard
  // was redundant for that case too.

  return (
    <Box
      ref={chatBoxRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        height: "100%", // Use 100% of parent instead of 92vh
        display: "flex",
        flexDirection: "column",
        borderRight: { xs: "none", sm: "none", md: (theme) => `1px solid ${theme.palette.divider}`, lg: (theme) => `1px solid ${theme.palette.divider}`, xl: (theme) => `1px solid ${theme.palette.divider}` },
        width: "100%",
        margin: 0,
        padding: 0,
        paddingBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
        maxWidth: "100%",
        overflow: "hidden", // Prevent outer container from scrolling
      }}
    >
      {!hideNavbar && (
        <ChatboxNavbar
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          isTeamMemberChat={isTeamMemberChat || isChatWithMembersTab}
          otherMember={currentTeamMemberChat?.otherMember}
        />
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          position: "relative",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            // Plain solid color now, same token ChatboxNavbar/the rest of
            // Inbox already use — no more doodle wallpaper image (Rahim,
            // 2026-08-22: "change the background to simple colour of top
            // nav etc meaning remove image").
            backgroundColor: (theme) => theme.palette.background.default,
            // Shared formula (utils/scrollbarStyles.js) — single source of
            // truth, matches Dashboard.jsx exactly.
            ...slimScrollbarSx,
          }}
        >
          <ChatBody
            key={chatBodyKey}
            contactId={selectedContactID || undefined}
            isTeamMemberChat={isTeamMemberChat || isChatWithMembersTab}
            teamMemberChatData={currentTeamMemberChat}
          />
        </Box>
        {/* Badge wraps the button (not the reverse) so it zooms in/out as one
            unit with it, instead of the count popping in separately (Rahim,
            2026-08-29: "doesnt tell how many new messages are below"). max=9
            keeps a double-digit unread count from stretching the badge oval
            oddly at this small size. */}
        {(contactSelected || isTeamMemberChat) && (
          <Zoom in={showScrollToBottom} unmountOnExit>
            <Badge
              badgeContent={newBelowCount}
              color="primary"
              max={9}
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{
                position: "absolute",
                right: { xs: 22, sm: 26 },
                bottom: { xs: 10, sm: 12 },
                // Was zIndex:2, tied with a z-index:2 on a message element in
                // ChatCard.jsx — equal values meant that message content could
                // win the stacking fight and paint over part of this button,
                // reading as "transparent" wherever they overlapped (Rahim,
                // 2026-08-29: "when on top of any chat it looks transparent").
                // The button's own fill IS fully opaque (confirmed via
                // computed style) — this was message content painting on top
                // of it, not the button itself being see-through. Bumped
                // clearly higher so a floating overlay control always wins,
                // not tuned to match one specific colliding value.
                zIndex: 5,
                "& .MuiBadge-badge": {
                  fontSize: "0.6875rem",
                  height: 19,
                  minWidth: 19,
                  padding: "0 5px",
                  // A defined edge so the badge reads as a distinct disc sitting
                  // on top of the button instead of blending into it — matters
                  // most in dark mode, where MUI's flat primary-color badge sat
                  // low-contrast against the button's own dark surface (Rahim,
                  // 2026-08-29: "make it better for dark mood give more
                  // border colour"). Same background.default "cutout ring"
                  // technique already used for the platform-icon badge on
                  // avatars elsewhere in Inbox (ChatboxNavbar.jsx).
                  border: (theme) => `2px solid ${theme.palette.background.default}`,
                },
              }}
            >
            <IconButton
              aria-label="Scroll to bottom"
              onClick={handleScrollToBottomClick}
              size="small"
              sx={{
                width: 48,
                height: 48,
                padding: 0,
                // First pass matched the low-contrast Close-button treatment
                // (paper + divider border) — Rahim wanted the original bold
                // high-contrast circle back instead (2026-08-29, reference
                // image: solid dark disc + white glyph), just built from real
                // theme tokens instead of the old hardcoded rgba pair. Same
                // symmetric flip as before — text.primary inverts cleanly
                // between modes (near-black in light, near-white in dark) —
                // just sourced from the palette now, not magic numbers.
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 10px rgba(0,0,0,0.35)"
                    : "0 2px 10px rgba(0,0,0,0.18)",
                // Outlined, not filled (Rahim, 2026-08-29: "icon colour black
                // the borders black the inner colour transparent"). Sourced
                // from text.primary rather than a literal #000 — that token
                // IS near-black in light mode (what was actually asked for)
                // but correctly inverts to near-white in dark mode instead of
                // a black-on-black border/icon disappearing there.
                // Same background+border pair as the composer card right
                // below it (TextBox.jsx's own outer Box: background.paper +
                // divider border) — Rahim's own call, "sounds good for the
                // dark mood" — instead of the white-border/background.default
                // combination from the last couple of passes.
                backgroundColor: (theme) => theme.palette.background.paper,
                color: (theme) => theme.palette.text.primary,
                border: (theme) => `1.5px solid ${theme.palette.divider}`,
                "&:hover": {
                  // action.hover is a translucent overlay token (rgba, meant
                  // to layer over an opaque surface) — using it as a flat
                  // standalone background let whatever's behind the button
                  // (a message's sender avatar) bleed straight through on
                  // hover, same underlying mistake as the badge fix earlier,
                  // just a different token/spot (Rahim, 2026-08-29: "same
                  // issue still persist when hovered"). A brightness filter
                  // adjusts the already-opaque paper color itself instead of
                  // layering a see-through one on top — genuinely solid
                  // regardless of what's underneath.
                  backgroundColor: (theme) => theme.palette.background.paper,
                  filter: (theme) => (theme.palette.mode === "dark" ? "brightness(1.35)" : "brightness(0.96)"),
                },
              }}
            >
              {/* ArrowRight2 rotated 90deg down (Rahim, 2026-08-29: "use the >
                  upside down icon") — the same right-chevron glyph already
                  used app-wide (e.g. LifecycleStageChip.jsx's next-stage
                  button), not a separate "down" icon family. */}
              <Box sx={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", transform: "rotate(90deg)" }}>
                <ArrowRight2 size="100%" color="currentColor" variant="Linear" />
              </Box>
            </IconButton>
            </Badge>
          </Zoom>
        )}
      </Box>
      {/* Render TextBox if contact is selected OR team member chat is active */}
      {(contactSelected || isTeamMemberChat) && (
        <TextBox 
          openDialog={openDialog} 
          setOpenDialog={setOpenDialog}
          isTeamMemberChat={isTeamMemberChat}
          droppedFilesPayload={droppedFilesPayload}
          onDroppedFilesConsumed={handleDroppedFilesConsumed}
        />
      )}
      {isDragActive && (contactSelected || isTeamMemberChat) && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: (theme) => `2px dashed ${theme.palette.primary.main}`,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(18, 18, 20, 0.72)"
                : "rgba(248, 252, 250, 0.72)",
            pointerEvents: "none",
            fontWeight: 600,
          }}
        >
          Drop files to attach in chat
        </Box>
      )}

      {/* Error Toast in Top Right Corner */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        sx={{ mt: 2, mr: 2 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ChatBox;
